import { useState, useEffect, useRef as useReactRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Check } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAllProgramStatuses } from "@/hooks/workflow/useAllProgramStatuses";
import LoaderComponent from "@/components/common/LoaderComponent";

const formSchema = z.object({
  programName: z.string().min(1, "Program name is required"),
  applicableStatusIds: z.array(z.coerce.string()).min(1, "At least one status must be selected"),
});

type FormData = z.infer<typeof formSchema>;

interface ProgramTypeFormProps {
  initialData?: {
    programName: string;
    applicableStatusIds: string[];
  };
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  isEdit?: boolean;
  isLoading?: boolean;
  hasChanges?: boolean;
  onDataChange?: (data: FormData) => void;
}

export function ProgramTypeForm({ initialData, onSubmit, onCancel, isEdit = false, isLoading = false, hasChanges = true, onDataChange }: ProgramTypeFormProps) {
  const { programStatuses, loading: statusesLoading } = useAllProgramStatuses();
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>(initialData?.applicableStatusIds || []);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dropdownRef = useReactRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    defaultValues: {
      programName: initialData?.programName || "",
      applicableStatusIds: initialData?.applicableStatusIds || [],
    },
  });

  useEffect(() => {
    form.setValue("applicableStatusIds", selectedStatusIds);
  }, [selectedStatusIds, form]);

  // Notify parent of data changes
  const watchedValues = form.watch();

  useEffect(() => {
    if (onDataChange) {
      onDataChange(watchedValues as FormData);
    }
  }, [watchedValues, onDataChange]);

  // Sync selected statuses when initialData changes (e.g., on first Edit mount)
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (initialData?.applicableStatusIds && !initialized) {
      const normalized = initialData.applicableStatusIds.map((id) => String(id));
      setSelectedStatusIds(normalized);
      form.setValue("applicableStatusIds", normalized);
      setInitialized(true);
    }
  }, [initialData?.applicableStatusIds, initialized, form]);

  const handleToggleStatus = (statusId: string | number) => {
    const id = String(statusId);
    const newStatusIds = selectedStatusIds.includes(id)
      ? selectedStatusIds.filter(sid => String(sid) !== id)
      : [...selectedStatusIds, id];
    setSelectedStatusIds(newStatusIds);
    form.setValue("applicableStatusIds", newStatusIds, { shouldDirty: true });
  };

  const handleRemoveStatus = (statusId: string) => {
    const newStatusIds = selectedStatusIds.filter(id => String(id) !== String(statusId));
    setSelectedStatusIds(newStatusIds);
    form.setValue("applicableStatusIds", newStatusIds, { shouldDirty: true });
  };

  const handleFormSubmit = (data: FormData) => {
    console.log("Form submitted with data:", data);
    onSubmit(data);
  };

  console.log("Form state:", {
    isValid: form.formState.isValid,
    errors: form.formState.errors,
    formValues: form.getValues(),
    selectedStatusIds,
    isLoading
  });

  if (statusesLoading) {
    return <LoaderComponent />;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          {isEdit ? "Edit Program Type" : "Add New Program Type"}
        </h1>
        <p className="text-muted-foreground mt-2">
          {isEdit ? "Update program type details" : "Create a new program type"}
        </p>
      </div>

      <Form {...form}>
        <form id="program_type_form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="programName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Program Name</FormLabel>
                <FormControl>
                  <Input id="program_type_form_name_input" placeholder="Enter program name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="applicableStatusIds"
            render={() => (
              <FormItem>
                <FormLabel>Applicable Program Status</FormLabel>
                 <div className="space-y-4">
                   {/* Text input to search & select statuses */}
                   <div className="relative" ref={dropdownRef}>
                     <Input
                        id="program_type_form_status_search"
                        placeholder="Search or select status..."
                        value={searchValue}
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          if (!open) setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        disabled={isLoading || statusesLoading}
                        autoComplete="off"
                      />
                      {statusesLoading && open && (
                        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
                          Loading statuses...
                        </div>
                      )}
                      {open && !statusesLoading && (() => {
                        const filtered = programStatuses.filter((s) =>
                          s.program_status.toLowerCase().includes(searchValue.toLowerCase())
                        );
                        return (
                          <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                            {filtered.length > 0 ? filtered.map((status) => {
                              const statusId = String(status.program_status_id);
                              const isSelected = selectedStatusIds.some(sid => String(sid) === statusId);
                              return (
                                <div
                                  key={status.program_status_id}
                                  className="flex items-center px-3 py-2 cursor-pointer hover:bg-accent"
                                  onClick={() => {
                                    handleToggleStatus(status.program_status_id);
                                    setSearchValue("");
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`}
                                  />
                                  {status.program_status}
                                </div>
                              );
                            }) : (
                              <div className="px-3 py-2 text-muted-foreground">No status found.</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                  {/* Selected Statuses */}
                  {selectedStatusIds.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Selected Statuses:</label>
                       <div className="flex flex-wrap gap-2">
                        {selectedStatusIds.map((statusId) => {
                          const status = programStatuses.find(s => String(s.program_status_id) === String(statusId));
                          return (
                            <Badge key={statusId} variant="secondary" className="flex items-center gap-1">
                              {status?.program_status || statusId}
                              <button
                                type="button"
                                onClick={() => handleRemoveStatus(String(statusId))}
                                className="ml-1 hover:bg-destructive/20 rounded-full"
                                disabled={isLoading}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4 justify-end">
            <Button id="program_type_form_cancel_btn" type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              id="program_type_form_submit_btn"
              type="submit" 
              disabled={
                isLoading || 
                (isEdit && !hasChanges) ||
                !form.getValues().programName?.trim() ||
                selectedStatusIds.length === 0
              }
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {isLoading ? "Saving..." : isEdit ? "Update Program Type" : "Save Program Type"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}